import React, { useState } from "react";
import {
  MapPin, Phone, Mail, Clock, CreditCard, User, Building2,
  ExternalLink, ChevronRight, History,
} from "lucide-react";
import { PlanLadder } from "../PlanLadder";
import { StatusHistory } from "../StatusHistory";
import { Section } from "./fields";
import { CompaniesAccessSection } from "./CompaniesAccessSection";

/** Read-only body of the facility drawer. */
export const ViewMode = ({ facility, edits, history }) => {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <>
      {facility.status === "SUSPENDED" && facility.status_change_reason && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-800">Suspension reason</div>
          <p className="mt-1 text-sm text-amber-900">{facility.status_change_reason}</p>
          <p className="mt-2 text-[11px] text-amber-700">
            by {facility.status_changed_by_name} · {facility.status_changed_at?.slice(0, 16).replace("T", " ")}
          </p>
        </div>
      )}
      {facility.status === "TERMINATED" && facility.status_change_reason && (
        <div className="rounded-md border border-red-300 bg-red-50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-800">Terminated — permanent</div>
          <p className="mt-1 text-sm text-red-900">{facility.status_change_reason}</p>
          <p className="mt-2 text-[11px] text-red-700">
            by {facility.status_changed_by_name} · {facility.status_changed_at?.slice(0, 16).replace("T", " ")}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Section label="Region">{facility.region}</Section>
        <Section label="District">{facility.district}</Section>
        <Section label="Town">{facility.town}</Section>
        <Section label="Location">{facility.location}</Section>
        <Section label="Ghana Post"><span className="font-mono text-xs">{facility.ghana_post_address}</span></Section>
        <Section label="Google Maps">
          {facility.maps_link ? (
            <a href={facility.maps_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-nmi-700 hover:underline text-xs break-all">
              Open in Google Maps <ExternalLink size={11} />
            </a>
          ) : "—"}
        </Section>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <div className="col-span-2">
          <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            <Phone size={11} /> Phones
          </div>
          {facility.phones && facility.phones.length ? (
            <div className="flex flex-wrap gap-1.5">
              {facility.phones.map((p) => (
                <a key={p} href={`tel:${p}`} className="rounded-sm bg-slate-100 px-2 py-1 text-xs font-mono text-slate-800 hover:bg-slate-200">{p}</a>
              ))}
            </div>
          ) : <div className="text-sm text-slate-500">—</div>}
        </div>
        <Section label={<><Mail size={11} className="inline mr-1" />Email</>}>
          {facility.email ? <a href={`mailto:${facility.email}`} className="text-nmi-700 hover:underline break-all">{facility.email}</a> : "—"}
        </Section>
        <Section label={<><User size={11} className="inline mr-1" />Relationship Manager</>}>{facility.relationship_manager}</Section>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Section label={<><Clock size={11} className="inline mr-1" />Hours</>}>{facility.working_hours}</Section>
        <Section label="Service Days">{facility.service_days}</Section>
        <Section label={<><Building2 size={11} className="inline mr-1" />Type</>}>{facility.facility_type}</Section>
        <div />
        <div className="col-span-2">
          <Section label={<><CreditCard size={11} className="inline mr-1" />Plans (tier ladder)</>}>
            <div className="mt-1"><PlanLadder nmiPlans={facility.nmi_plans} showLegend /></div>
          </Section>
        </div>
      </div>

      <CompaniesAccessSection facilityId={facility.facility_id} />

      {edits.length > 0 && (
        <div>
          <button
            onClick={() => setShowHistory((v) => !v)}
            className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-left hover:bg-slate-100"
          >
            <div className="flex items-center gap-2">
              <History size={14} strokeWidth={2.5} className="text-slate-700" />
              <span className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
                Edit history · {edits.length}
              </span>
            </div>
            <ChevronRight size={16} className={`transition-transform ${showHistory ? "rotate-90" : ""}`} />
          </button>
          {showHistory && (
            <div className="mt-2 rounded-md border border-slate-200 divide-y divide-slate-200">
              {edits.slice(0, 20).map((e) => (
                <div key={e.edit_id} className="p-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                    {e.actor_name} · {e.timestamp?.slice(0, 16).replace("T", " ")}
                  </div>
                  <div className="mt-1 space-y-1">
                    {Object.entries(e.changes || {}).map(([field, diff]) => (
                      <div key={field} className="text-xs">
                        <span className="font-mono text-slate-500">{field}:</span>{" "}
                        <span className="line-through text-slate-400">{String(diff.old ?? "—")}</span>
                        {" → "}
                        <span className="font-semibold text-slate-900">{String(diff.new ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-md border border-slate-200 p-4">
        <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
          Status history
        </div>
        <StatusHistory events={history || []} />
      </div>
    </>
  );
};
